import { object, Output, safeParse } from "valibot";

import {
  debugLog,
  getLocalStorageValueWithExpiry,
  safeParseJSON,
} from "shared/functions";

import {
  // ChatDataSchema,
  SerializedChatDataSchema,
  SSEErrorSchema,
  UUIDSchema,
} from "./schemas";
import {
  allowedDashboardMessages,
  allowedEverywhereMessages,
} from "./constants";

export const getIsLightColor = (hex: string): boolean => {
  if (!hex) {
    debugLog("Unexpected hex value", hex);
    return false;
  } else if (hex.indexOf("#") === 0) {
    hex = hex.slice(1);
  } else if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  if (hex.length !== 6) {
    return false;
  }
  const r: number = Number.parseInt(hex.slice(0, 2), 16);
  const g: number = Number.parseInt(hex.slice(2, 4), 16);
  const b: number = Number.parseInt(hex.slice(4, 6), 16);

  // https://stackoverflow.com/a/3943023/112731
  return r * 0.299 + g * 0.587 + b * 0.114 > 186;
};

export const invertColor = (hex: string) =>
  getIsLightColor(hex) ? "#111827" : "#FFFFFF";

export const addAlpha = (hex: string, opacity: number) => {
  if (!hex) {
    return hex;
  } else if (hex.indexOf("#") === 0) {
    hex = hex.slice(1);
  }
  const _opacity = Math.round(Math.min(Math.max(opacity || 1, 0), 1) * 255);
  return "#" + hex + _opacity.toString(16).toUpperCase();
};

export const openedInDashboard = (baseOrigin: URL) => {
  const isLocalFrontend =
    ["localhost", "127.0.0.1"].includes(baseOrigin.hostname) &&
    ["3000", "3010"].includes(baseOrigin.port);
  return (
    isLocalFrontend ||
    ["writesonic.com", "botsonic.dev"].some(domain =>
      baseOrigin.hostname.endsWith(domain),
    )
  );
};

export const isLocalCDN = () =>
  ["http://localhost:3010/CDN", "http://localhost:3030/CDN"].includes(
    window.location.href,
  );

export const parseSseErrorMessage = (eventData: string, baseOrigin: string) => {
  const isLocalOrDashboard =
    openedInDashboard(new URL(baseOrigin)) || isLocalCDN();

  const data: unknown =
    safeParseJSON(eventData) ||
    (typeof eventData === "string" &&
      safeParseJSON(eventData.replaceAll("'", '"')));
  const result = safeParse(SSEErrorSchema, data);

  if (result.success) {
    const errorBeginsWith = (allowedMessage: string) =>
      result.output.error_message.startsWith(allowedMessage);
    const isAllowedMessage =
      allowedEverywhereMessages.some(errorBeginsWith) ||
      (isLocalOrDashboard && allowedDashboardMessages.some(errorBeginsWith));
    if (isAllowedMessage) {
      return result.output.error_message;
    }
  }

  return "";
};

export const getPreservedChatDataKey = (instanceName: string, token: string) =>
  `${instanceName}-${token}-chatData`;

export const getRestoredChatData = (
  instanceName: string,
  token: string,
): Output<typeof SerializedChatDataSchema> | null => {
  const preservedChatDataKey = getPreservedChatDataKey(instanceName, token);
  try {
    const stringifiedChatData =
      getLocalStorageValueWithExpiry(preservedChatDataKey);
    const chatDataResult = safeParse(
      object({
        // chatData: ChatDataSchema,
        chatId: UUIDSchema,
      }),
      JSON.parse(String(stringifiedChatData)),
    );

    if (chatDataResult.success) {
      return chatDataResult.output;
    }
  } catch (err) {
    debugLog("Unable to restore chat data");
    debugLog(err);
  }
  return null;
};

export const parseJson = (data: unknown) => {
  try {
    return { success: true, parsedJson: safeParseJSON(data) };
  } catch (err) {
    return { success: false, err };
  }
};
